import { computed, inject } from 'vue';
import { loadComponent } from '@rebuild/utils/loader.js';
import { useStore } from '@/utils/vue-api';

/**
 * 获取组件配置信息
 * @param {Object} config - 模板配置
 * @param {string} compId - 组件ID
 * @param {string} componentType - 组件类型 'layout' | 'display'
 * @returns {Object|null} 组件配置 {version, type}
 */
function getComponentConfig(config, compId, componentType) {
	if (!config) return null;
	const { layout, modules } = config;

	// 查找布局组件
	if (componentType === 'layout' && layout) {
		const findLayout = layout.find(item => item.id === compId);
		if (findLayout) {
			return {
				version: findLayout.version,
				type: 'layout'
			};
		}
	}
	// 查找展示组件
	if (componentType === 'display' && modules) {
		const findModule = modules.find(item => item.id === compId);
		if (findModule) {
			return {
				version: findModule.version,
				type: 'display'
			};
		}
		// 兼容动态ID的组件查找
		if (compId === 'custom') {
			const findModule = modules.find(item => item.type === 1);
			if (findModule) {
				return {
					version: findModule.version,
					type: 'display'
				};
			}
		}
		if (compId === 'widget_shell') {
			const findModule = modules.find(item => item.type === 2);
			if (findModule) {
				return {
					version: findModule.version,
					type: 'display'
				};
			}
		}
	}
	return null;
}

/**
 * 组件解析器hook
 * @param {string} componentType - 限制组件类型 'layout' | 'display'
 * @returns {object} 包含loadComponent方法的对象
 */
export function useComponentResolver(componentType) {
	const templateType = inject('TEMPLATE_TYPE');
	const getTemplateType = computed(() => templateType?.value || '');

	const store = useStore();
	const getTemplateConfig = computed(() => store.getters.getUiConfig);

	/**
	 * 解析并加载组件
	 * @param {string} compId - 组件ID
	 * @param {string} mode - 模式
	 * @returns {Promise<object|null>} 返回包含component和config的对象
	 */
	const resolveComponent = (compId, mode) => {
		const tmplType = getTemplateType.value;
		const config = getComponentConfig(getTemplateConfig.value, compId, componentType);
		if (!config) {
			console.warn(`无法加载组件: ${tmplType}/${compId}`);
			return null;
		}

		const { version, type = componentType } = config || {};
		const result = loadComponent({
			meta: {
				templateType: tmplType,
				moduleId: compId,
				version,
				type
			},
			mode
		});

		if (result?.component) {
			return {
				component: result.component,
				config
			};
		} else {
			console.warn(`无法加载组件: ${tmplType}/${compId}:${version} (${mode}) [${type}]`);
			return null;
		}
	};

	return {
		supportedSlotNames: ['footer'],
		resolveComponent
	};
}
